// Paso 2 (PE-A4, PE-28a): enlaces del sector con bodegas o zonas y "Proponer carga de fuego desde el inventario".
// La propuesta no guarda nada: el responsable marca las filas que quiere, se agregan al borrador del sector y solo se
// guardan con "Guardar sector". Viewer solo ve los enlaces; manager enlaza; solo admin quita un enlace.

import React, { useState } from 'react';
import {
  Bodega, EnlaceSector, lugarDe, nombreMaterial, ProductoInventario, PropuestaCarga, proponerCargaDesdeInventario, UbicacionProducto,
} from '../../lib/pe/pe-inventario';
import { formatoEc } from '../../lib/pe/pe-unidades';
import { Campo } from './ComponentesPE';
import { plural } from '../../lib/plurales';

export interface PropsInventario {
  sectorId?: string;
  enlaces: EnlaceSector[];
  bodegas: Bodega[];
  error?: string;
  ro: boolean;
  puedeBorrar: boolean;
  yaEnSector: Set<string>;
  onEnlazar: (warehouseId: string, zoneId: string | null) => Promise<void>;
  onQuitarEnlace: (e: EnlaceSector) => void;
  cargarInventario: (enlaces: EnlaceSector[]) => Promise<{ productos: ProductoInventario[]; ubicaciones: UbicacionProducto[] }>;
  onAgregar: (p: PropuestaCarga[]) => void;
}

export default function Paso2Inventario(p: PropsInventario) {
  const [bodega, setBodega] = useState('');
  const [zona, setZona] = useState('');
  const [propuestas, setPropuestas] = useState<PropuestaCarga[] | null>(null);
  const [elegidas, setElegidas] = useState<Set<string>>(new Set());
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const b = p.bodegas.find(x => x.id === bodega);

  const lugar = (e: EnlaceSector) => {
    const w = p.bodegas.find(x => x.id === e.warehouse_id);
    return w ? lugarDe(w, e.zone_id) : 'bodega ya no disponible';
  };
  const enlazar = async () => {
    if (!bodega) return;
    setOcupado(true); setError('');
    try { await p.onEnlazar(bodega, zona || null); setBodega(''); setZona(''); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setOcupado(false); }
  };
  const proponer = async () => {
    setOcupado(true); setError('');
    try {
      const inv = await p.cargarInventario(p.enlaces);
      const lista = proponerCargaDesdeInventario(p.enlaces, p.bodegas, inv.productos, inv.ubicaciones, p.yaEnSector);
      setPropuestas(lista);
      setElegidas(new Set(lista.map(x => x.productId)));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setOcupado(false); }
  };
  const agregar = () => {
    if (!propuestas) return;
    p.onAgregar(propuestas.filter(x => elegidas.has(x.productId)));
    setPropuestas(null); setElegidas(new Set());
  };

  return (
    <section className="pe2-sub-card pe2-card" aria-label="Bodegas o zonas del inventario">
      <h3>Bodegas o zonas del inventario</h3>
      {p.error && <div className="pe2-error" role="alert">{p.error}</div>}
      {!p.sectorId ? (
        <p className="pe2-nota" style={{ margin: 0 }}>Guarda el sector para enlazarlo con bodegas o zonas.</p>
      ) : (
        <>
          {p.enlaces.length === 0 && <p className="pe2-nota" style={{ margin: 0 }}>Sin enlaces.</p>}
          {p.enlaces.length > 0 && (
            <ul className="pe2-evidencias-lista">
              {p.enlaces.map(e => (
                <li key={e.id ?? `${e.warehouse_id}-${e.zone_id}`}>
                  <span className="pe2-evidencia-nombre">{lugar(e)}{e.zone_id ? '' : ' (todas sus zonas)'}</span>
                  {p.puedeBorrar && !p.ro && <button type="button" className="pe2-btn peligro" onClick={() => p.onQuitarEnlace(e)}>Quitar enlace</button>}
                </li>
              ))}
            </ul>
          )}
          {!p.ro && (
            <div className="pe2-grid">
              <Campo etiqueta="Bodega">
                <select value={bodega} onChange={e => { setBodega(e.target.value); setZona(''); }}>
                  <option value="">Elige…</option>
                  {p.bodegas.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </Campo>
              <Campo etiqueta="Zona (opcional)" nota="Sin zona: toda la bodega.">
                <select value={zona} disabled={!b} onChange={e => setZona(e.target.value)}>
                  <option value="">Toda la bodega</option>
                  {(b?.zonas ?? []).map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              </Campo>
              <div className="pe2-acciones">
                <button type="button" className="pe2-btn" disabled={!bodega || ocupado} onClick={enlazar}>Enlazar</button>
              </div>
            </div>
          )}
          {!p.ro && p.enlaces.length > 0 && (
            <div className="pe2-acciones">
              <button type="button" className="pe2-btn" disabled={ocupado} onClick={proponer}>Proponer carga de fuego desde el inventario</button>
            </div>
          )}
        </>
      )}
      {error && <div className="pe2-error" role="alert">{error}</div>}

      {propuestas && (
        <div className="pe2-lista-editable" aria-label="Propuesta desde el inventario">
          <b>Propuesta ({propuestas.length})</b>
          <p className="pe2-nota" style={{ margin: 0 }}>
            No se guarda nada todavía: marca las filas, agrégalas a la carga de fuego, revísalas y guarda el sector.
          </p>
          {propuestas.length === 0 && <p className="pe2-nota">No hay productos nuevos en esas bodegas o zonas.</p>}
          {propuestas.map(x => (
            <label key={x.productId} className="pe2-propuesta">
              <input type="checkbox" checked={elegidas.has(x.productId)}
                onChange={e => { const n = new Set(elegidas); if (e.target.checked) n.add(x.productId); else n.delete(x.productId); setElegidas(n); }} />
              <span>
                <b>{x.producto}</b> · {x.lugar}
                <br />
                {x.material
                  ? `Material RD 164 (Tabla 1.3.3, por ${x.coincidencia === 'cas' ? 'CAS' : 'nombre'}): ${nombreMaterial(x.material)} · qi ${formatoEc(x.material.qi, 2)} MJ/kg`
                  : 'Sin material del RD 164'}
                {' · '}{x.masaKg !== null ? `${formatoEc(x.masaKg, 2)} kg` : 'sin kg'}
                {x.estado === 'por_validar' && <> · <span className="pe2-resaltado">por validar</span></>}
                {x.notas.map((n, i) => <small key={i} style={{ display: 'block' }} className="pe2-nota">{n}</small>)}
              </span>
            </label>
          ))}
          <div className="pe2-acciones">
            <button type="button" className="pe2-btn" onClick={() => setPropuestas(null)}>Descartar</button>
            {propuestas.length > 0 && (
              <button type="button" className="pe2-btn primario" disabled={elegidas.size === 0} onClick={agregar}>
                Agregar {plural(elegidas.size, 'fila', 'filas')} a la carga de fuego
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
